import { useCallback, useEffect, useRef, useState } from 'react';
import { BORNES_PAPIER, type FondDePapier } from '@nextilt/commun';
import type { ChangementDePapier, Papier } from '../partage/api/types';
import { ECRIRE_SUR_LE_POST_IT, SUPPRIMER_LE_POST_IT, TITRE_DU_POST_IT } from '../partage/vocabulaire';
import { CadreDuPapier } from './CadreDuPapier';
import { ceQuiAChange } from './reglesDesPapiers';
import type { CarteDesPapiers } from './useGlisserUnPapier';

/**
 * Un post-it posé sur la carte (chantier post-it-et-listes, ticket 02 ; maquette
 * `.scratch/chantiers/carnet/maquettes/projet-ouvert.html`, `.pi`). Un titre facultatif et un
 * texte, écrits sur la carte même : un clic dedans pour écrire, sans panneau. Ce qui est écrit
 * part à la sortie du champ, comme les champs tapés de la fiche, et aussi quand la carte s'en va
 * (une autre bulle) pendant qu'on écrit.
 *
 * Sa place, sa taille, le glisser, la poignée, son papier et la croix sont ceux de tout papier
 * (`CadreDuPapier`, tickets 03 et 04).
 */
export function PostIt({
  papier,
  carte,
  aEcrire,
  onEcrire,
  onPlacer,
  onChangerDeFond,
  onSupprimer,
}: {
  papier: Papier;
  carte: CarteDesPapiers;
  /** Il vient d'être posé : le curseur se met dans son texte. */
  aEcrire: boolean;
  /** Le titre et le texte qui ont changé partent au serveur. */
  onEcrire: (id: string, changement: ChangementDePapier) => void;
  /** La place ou la taille, au lâcher ; la promesse est tenue quand le serveur a répondu. */
  onPlacer: (id: string, changement: ChangementDePapier) => Promise<void>;
  onChangerDeFond: (id: string, fond: FondDePapier) => void;
  onSupprimer: (id: string) => void;
}) {
  const [titre, setTitre] = useState(papier.titre ?? '');
  const [texte, setTexte] = useState(papier.texte);
  const ecrit = useRef(false);

  /*
   * Ce que le serveur rend remplace le brouillon, sauf pendant qu'on écrit. Seulement quand le
   * papier change, jamais à la sortie d'un champ : passer du texte au titre relisait le texte
   * d'avant l'envoi, puis le renvoyait vide.
   */
  useEffect(() => {
    if (ecrit.current) return;
    setTitre(papier.titre ?? '');
    setTexte(papier.texte);
  }, [papier.titre, papier.texte]);

  // Enregistrer ce qui a changé : à la sortie du champ, et quand le post-it quitte l'écran.
  const dernier = useRef({ papier, titre, texte, onEcrire });
  dernier.current = { papier, titre, texte, onEcrire };
  const enregistrer = useCallback(() => {
    const d = dernier.current;
    const changement = ceQuiAChange(d.papier, { titre: d.titre, texte: d.texte });
    if (changement) d.onEcrire(d.papier.id, changement);
  }, []);
  useEffect(() => () => enregistrer(), [enregistrer]);

  const sortir = () => {
    ecrit.current = false;
    enregistrer();
  };

  return (
    <CadreDuPapier
      papier={papier}
      carte={carte}
      supprimer={SUPPRIMER_LE_POST_IT}
      onPlacer={onPlacer}
      onChangerDeFond={onChangerDeFond}
      onSupprimer={onSupprimer}
    >
      <input
        className="papier-titre"
        value={titre}
        maxLength={BORNES_PAPIER.titreMax}
        placeholder={TITRE_DU_POST_IT}
        aria-label={TITRE_DU_POST_IT}
        onFocus={() => {
          ecrit.current = true;
        }}
        onChange={(e) => setTitre(e.target.value)}
        onBlur={sortir}
      />
      <textarea
        className="papier-texte"
        value={texte}
        maxLength={BORNES_PAPIER.texteMax}
        placeholder={ECRIRE_SUR_LE_POST_IT}
        aria-label={ECRIRE_SUR_LE_POST_IT}
        autoFocus={aEcrire}
        rows={3}
        onFocus={() => {
          ecrit.current = true;
        }}
        onChange={(e) => setTexte(e.target.value)}
        onBlur={sortir}
      />
    </CadreDuPapier>
  );
}
